/**
 * Template de prise de besoin : Procédure de gestion des preuves v1
 * Aligné sur ITIL 4, ISO 27001 et NIST CSF 2.0
 * Procédure opérationnelle détaillée pour la gestion des preuves lors d'incidents
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateProcedureGestionPreuvesV1: IntakeTemplate = {
  id: 'procedure-gestion-preuves-v1',
  name: 'Prise de besoin Procédure de gestion des preuves v1',
  description:
    'Questionnaire complet pour élaborer une procédure de gestion des preuves conforme ITIL 4, ISO 27001 et NIST CSF 2.0. Définit les processus de collecte, préservation, analyse et documentation des preuves numériques selon les standards forensiques.',
  documentTypeCode: 'EVIDENCE_MANAGEMENT_PROCEDURE',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Types de Preuves',
      description: 'Informations sur l\'entreprise et types de preuves',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la procédure.',
        },
        {
          id: 'types_preuves',
          label: 'Quels types de preuves numériques sont collectés lors d\'incidents ?',
          type: 'multiselect',
          options: [
            'Journaux système (logs)',
            'Journaux réseau',
            'Journaux applicatifs',
            'Images disques',
            'Fichiers suspects',
            'Emails',
            'Captures réseau',
            'Métadonnées',
            'Screenshots',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0: Types de preuves pour investigation.',
        },
        {
          id: 'qui_collecte',
          label: 'Qui est responsable de la collecte des preuves ?',
          type: 'text',
          placeholder: 'Ex: Équipe sécurité, Experts forensiques, Techniciens TI...',
          required: true,
          helpText: 'ITIL 4: Responsable pour collecte appropriée.',
        },
        {
          id: 'ton_document_procedure',
          label: 'Quel ton souhaitez-vous pour cette procédure ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation de la procédure.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Collecte & Préservation',
      description: 'Processus de collecte et préservation des preuves',
      icon: 'Shield',
      questions: [
        {
          id: 'processus_collecte',
          label: 'Quel est le processus de collecte des preuves ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Identification → Documentation → Collecte → Emballage → Étiquetage...',
          helpText: 'NIST CSF 2.0: Processus structuré pour collecte appropriée.',
        },
        {
          id: 'outils_collecte',
          label: 'Quels outils sont utilisés pour la collecte ?',
          type: 'multiselect',
          options: [
            'Outils forensiques (FTK, EnCase)',
            'Outils de capture réseau (Wireshark)',
            'Outils d\'imagerie disque',
            'Outils de collecte logs',
            'Outils cloud',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0: Outils appropriés pour collecte efficace.',
        },
        {
          id: 'preservation_preuves',
          label: 'Comment sont préservées les preuves ?',
          type: 'multiselect',
          options: [
            'Chiffrement',
            'Hachage (MD5, SHA-256)',
            'Stockage sécurisé',
            'Chaîne de traçabilité',
            'Copies multiples',
            'Stockage hors site',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001: Préservation pour intégrité des preuves.',
        },
        {
          id: 'chaine_tracabilite',
          label: 'Comment est maintenue la chaîne de traçabilité (chain of custody) ?',
          type: 'textarea',
          placeholder: 'Décrivez: Documentation → Signature → Transfert → Stockage...',
          helpText: 'NIST CSF 2.0: Chaîne de traçabilité essentielle pour validité légale.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Analyse & Documentation',
      description: 'Processus d\'analyse et documentation des preuves',
      icon: 'FileText',
      questions: [
        {
          id: 'processus_analyse',
          label: 'Quel est le processus d\'analyse des preuves ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Préparation → Analyse → Interprétation → Documentation...',
          helpText: 'NIST CSF 2.0: Processus structuré pour analyse efficace.',
        },
        {
          id: 'outils_analyse',
          label: 'Quels outils sont utilisés pour l\'analyse ?',
          type: 'multiselect',
          options: [
            'Outils forensiques',
            'Outils d\'analyse de logs',
            'Outils d\'analyse réseau',
            'Outils d\'analyse malware',
            'Outils d\'analyse mémoire',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0: Outils appropriés pour analyse approfondie.',
        },
        {
          id: 'documentation_preuves',
          label: 'Quelle documentation est créée pour les preuves ?',
          type: 'multiselect',
          options: [
            'Rapport d\'investigation',
            'Rapport forensique',
            'Chaîne de traçabilité',
            'Analyse des preuves',
            'Recommandations',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001: Documentation essentielle pour traçabilité.',
        },
        {
          id: 'conservation_preuves',
          label: 'Combien de temps sont conservées les preuves ?',
          type: 'select',
          options: [
            '1 an',
            '3 ans',
            '5 ans',
            '7 ans',
            'Indéfiniment',
            'Selon type d\'incident',
            'À déterminer',
          ],
          helpText: 'ISO 27001: Conservation pour conformité et audit.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Gestion & Amélioration',
      description: 'Gestion et amélioration continue',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'stockage_preuves',
          label: 'Où sont stockées les preuves ?',
          type: 'multiselect',
          options: [
            'Serveur sécurisé interne',
            'Stockage cloud sécurisé',
            'Support physique (disques, bandes)',
            'Stockage hors site',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001: Stockage sécurisé pour protection des preuves.',
        },
        {
          id: 'acces_preuves',
          label: 'Qui a accès aux preuves et comment est contrôlé l\'accès ?',
          type: 'textarea',
          placeholder: 'Décrivez: Responsables → Contrôles d\'accès → Journalisation...',
          helpText: 'ISO 27001: Contrôle d\'accès pour protection des preuves.',
        },
        {
          id: 'destruction_preuves',
          label: 'Quel est le processus de destruction des preuves ?',
          type: 'textarea',
          placeholder: 'Décrivez: Autorisation → Destruction sécurisée → Documentation...',
          helpText: 'ISO 27001: Destruction sécurisée pour protection des données.',
        },
        {
          id: 'revision_procedure',
          label: 'À quelle fréquence souhaitez-vous réviser cette procédure ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





